import { makeStyles } from 'tss-react/mui';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import shortid from 'shortid';
import { EDAutoCompleteEdit } from '../../../../../BaseComponents/AutoCompleteEdit/EDAutoCompleteEdit';
import EDChip from '../../../../../BaseComponents/Chip/EDChip';
import EDBox from '../../../../../BaseComponents/EDBox/EDBox';
import EDIcon from '../../../../../BaseComponents/Icon/EDIcon';
import { IconType } from '../../../../../BaseComponents/Icon/Utils/IconDefinitions';
import { ITreeSection } from '../../../../../BaseComponents/TreeSelect/TreeSelectUtils';
import {
  BusinessDataFormContext,
  IBusinessDataFormContext,
} from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/BusinessDataForm';
import { FormUpdateAssociatonType } from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormValueUtils';
import resursQuery from '../../Rutiner/Kalender/Queries/ResursQuery/ResursQuery.gql';
import {
  ResursQuery,
  ResursQueryVariables,
  ResursQuery_db_BOResurs_All,
} from '../../Rutiner/Kalender/Queries/ResursQuery/_QueryTypes/ResursQuery';
import EDDialog from '../../../../../BaseComponents/Dialog/EDDialog';
import EDButton from '../../../../../BaseComponents/Buttons/Button/EDButton';
import EDTreeSelect from '../../../../../BaseComponents/TreeSelect/EDTreeSelect';
import {
  ActiveDatabaseContext,
  IRoutineAvailability,
} from '../../../../../Components/ActiveDatabaseContext/ActiveDatabaseContext';
import { RutinSettingsContext } from '../../../_Common/Components/RutinAvailabilityContext/RutinAvailabilityContext';
import mergeSort from '../../../../../Utils/mergeSort';
import { uniqWith, clone } from 'ramda';

import {
  bokningsrubrikFromDbBokningsrubrik,
  bokningsrubrikFromDbResurs,
  getBokningsrubrikerFromSections,
  getCheckedResourcesFromBokningsrubriker,
  isReadOnlyRutin,
} from './Utils/AktivitetUtils';
import { AktivitetType__Associations_AktBokRubrik } from './_QueryTypes/AktivitetType';
import {
  IAktTypResursRubrik,
  IResursTyp,
  IResursTypContext,
  ResursTypContext,
} from '../../Contexts/ResursTypContext/ResursTypContext';
import { IFieldValidation } from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Utils/BusinessDataFormUtils';
import { NEWLY_ADDED_ID_PREFIX_FOR_UPDATE } from '../../../_Common/Components/BusinessCollectionComponents/BusinessDataForm/Const/BusinessDataFormConst';
import EDIconButton from '../../../../../BaseComponents/Buttons/IconButton/EDIconButton';
import { timeToStr, tryStrToTime } from '../../../../../Utils/stringUtils';
import { ResursAvvikandeTid } from './ResursAvvikandeTid';
import { useIsSmallScreen } from '../../../../../Hooks/useFullscreenView';
import {
  TEDBOResursVARNING_DUBBELBOKNINGEnum,
  TEDBOResursWEBBOKNINGSBAREnum,
} from '../../../../../globalQueryTypes';
import { sortBokningsrubriker } from '../../Rutiner/Kalender/Components/SchedulerComponent/Utils/resourcesSortUtils';
import {
  MeQuery,
  MeQuery_me,
} from '../../Rutiner/Kalender/Queries/MeQuery/_QueryTypes/MeQuery';
import { AktivitetQuery_node_TEDBOAktivitet } from './Queries/_QueryTypes/AktivitetQuery';

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const useStyles = makeStyles()(theme => ({
  chipMarginTop: {
    '&[class*="MuiOutlinedInput-root"]': {
      paddingTop: theme.spacing(1),
      paddingBottom: theme.spacing(1),
    },
  },
  ResourceAddBtnWidth: {
    '&[class*="MuiAutocomplete-root"]': {
      width: '100%',
    },
  },
}));

// ---------------------------------------------------------------------------
// Public interfaces
// ---------------------------------------------------------------------------

export interface IBokningsrubrik {
  rubrikId?: string;
  resursText: string;
  resursNamn: string;
  BO_RESURS_ID: string;
  resursId: string;
  resursTyp: string;
  resursRubrik?: IAktResursRubrik;
  posNr?: number;
  platsId?: string;
  resourceInList?: boolean;
  resourceTitle?: string | null;
  prio_ordning?: number | undefined;
}

export interface IAktResursRubrik {
  id: string;
  posNr: number;
}

export interface ICurrentDayBooking {
  aktId: string;
  BO_RESURS_ID: string;
  resStart: string;
  resEnd: string;
  bokNr: string;
  aktTitel: string;
  varning_dubbelbokning?:
    | TEDBOResursVARNING_DUBBELBOKNINGEnum
    | null
    | undefined;
}

export interface IOverlappedBooking {
  rubrikId: string;
  resourceName: string;
  bastyp: string;
  resStart: string;
  resEnd: string;
  bokNr: string;
  aktTitel: string;
}

interface IProps {
  fullWidth?: boolean;
  rightMargin?: boolean | undefined;
  activeDatabase?: string;
  formContext: IBusinessDataFormContext<any>;
  chipMarginTop?: string | undefined;
  currentBokningsRubrikPosnr: number[];
  allAktResursRubriker: IAktTypResursRubrik[] | undefined;
  onAddRes?: (
    formContext: IBusinessDataFormContext<any>,
    resId: string,
    resType: string
  ) => void;
  onDeleteRes?: (
    formContext: IBusinessDataFormContext<any>,
    resId: string,
    resType: string
  ) => void;
  userSign?: string;
  testId?: string;
  disableMode: boolean;
  userSettings?: MeQuery_me | undefined;
  nodeData?: AktivitetQuery_node_TEDBOAktivitet | null | undefined;
  me_data?: MeQuery | undefined;
  availableRoutines?: IRoutineAvailability | undefined;
}

// ---------------------------------------------------------------------------
// Exported update-array helpers
// ---------------------------------------------------------------------------

export const getUpdateForsamlingArray = (
  forsamlingId: string,
  forsamlingNamn?: string
): FormUpdateAssociatonType[][] => [
  [
    {
      operation: 'Ref',
      associationName: 'Forsamling',
      id: forsamlingId,
      values: { BENAMNING: forsamlingNamn },
    },
  ],
];

export const getAddResursUpdateArray = (
  resursNamn: string,
  resursKod: string,
  resursId: string,
  resursTyp: string,
  basTyp: string,
  FRANDATUM?: string,
  TILLDATUM?: string,
  FRANTID?: string,
  TILLTID?: string,
  AKTRESRUB_POSNR?: number
): FormUpdateAssociatonType[][] => {
  const idBokRub = `${NEWLY_ADDED_ID_PREFIX_FOR_UPDATE}AktBokRubrik${shortid.generate()}`;
  return [
    [
      {
        operation: 'Add_Values',
        associationName: 'AktBokRubrik',
        id: idBokRub,
        values: {
          RESURS_NAMN: resursNamn,
          FRANDATUM,
          TILLDATUM,
          FRANTID,
          TILLTID,
          KOD: FRANDATUM === TILLDATUM ? 'ENDAGSBOKNING' : 'FLERDAGSBOKNING',
          AKTRESRUB_POSNR,
        },
      },
      {
        operation: 'Ref',
        associationName: 'Resurs',
        id: resursId,
        values: {
          BO_RESURS_ID: resursKod,
          BO_RESURS_NAMN: resursNamn,
          id: resursId,
        },
      },
      {
        operation: 'Ref',
        associationName: 'ResRestyp',
        id: shortid.generate(),
        values: { Bastyp: basTyp, BO_RESURSTYP_NAMN: resursTyp },
      },
    ],
  ];
};

export const getDeleteResursUpdateArray = (
  rubrikId: string
): FormUpdateAssociatonType[][] => [
  [
    {
      operation: 'Delete_Values',
      associationName: 'AktBokRubrik',
      id: rubrikId,
    },
  ],
];

// ---------------------------------------------------------------------------
// Module-level pure helpers
// ---------------------------------------------------------------------------

const resursBokningsbar = (
  res: ResursQuery_db_BOResurs_All,
  userSign: string | undefined
): boolean =>
  res?.WEBBOKNINGSBAR === TEDBOResursWEBBOKNINGSBAREnum.FRITT ||
  res?.WEBBOKNINGSBAR === TEDBOResursWEBBOKNINGSBAREnum.WEBFRI ||
  !!(userSign && res._Associations?.Anvandare?.SIGNATURE === userSign);

const isPersonResourceBookable = (
  res: ResursQuery_db_BOResurs_All | undefined,
  userSign: string | undefined
): boolean => {
  if (!userSign || !res) return false;
  return (
    (res.WEBBOKNINGSBAR === TEDBOResursWEBBOKNINGSBAREnum.FRITT ||
      res.WEBBOKNINGSBAR === TEDBOResursWEBBOKNINGSBAREnum.WEBFRI ||
      res.WEBBOKNINGSBAR === TEDBOResursWEBBOKNINGSBAREnum.NORMAL ||
      res.WEBBOKNINGSBAR === TEDBOResursWEBBOKNINGSBAREnum.PRIVAT) &&
    res._Associations?.Anvandare?.SIGNATURE === userSign
  );
};

const canUserInteractWithBooking = (
  me_data: MeQuery | undefined,
  nodeData: AktivitetQuery_node_TEDBOAktivitet | null | undefined,
  availableRoutines: IRoutineAvailability | undefined
): boolean =>
  !!(
    me_data?.me?.[0]?.userCanEditBooking ||
    nodeData?.BOKNINGSSIGNATUR === me_data?.me?.[0]?.sign ||
    !isReadOnlyRutin('rtnWebKalender', availableRoutines)
  );

const timeToStr2 = (strTid?: string): string => {
  if (!strTid) return '';
  const tid = tryStrToTime(strTid);
  return tid ? timeToStr(tid) : '';
};

const getUniqResourceBaseTypes = (resources: ResursQuery_db_BOResurs_All[]) => {
  const importOrder = ['Plats', 'Person', 'Övrigt'];
  const sortByObject = importOrder.reduce<Record<string, number>>(
    (obj, item, index) => ({ ...obj, [item]: index }),
    {}
  );
  const resourcesBaseTypes = resources?.map(item => ({
    name: item._Associations?.ResRestyp?.Bastyp,
    value: item.id,
  }));
  const customSort = mergeSort(
    resourcesBaseTypes,
    (a, b) => sortByObject[a.name as string] - sortByObject[b.name as string]
  );
  return uniqWith((a, b) => a.name === b.name, customSort);
};

const GetSubList = (
  resourceData: ResursQuery,
  resTypes: IResursTyp[],
  checkedResources: string[],
  data: string,
  userSign: string | undefined
): ITreeSection[] => {
  const subList: ITreeSection[] = [];

  resTypes?.forEach(resType => {
    if (resType.foraldratyp !== data) return;

    const resSubTypes =
      GetSubList(
        resourceData,
        resTypes,
        checkedResources,
        resType.namn,
        userSign
      ) ?? [];
    const resources =
      resourceData?.db?.BOResurs_All?.reduce<ITreeSection[]>((acc, item2) => {
        if (
          item2._Associations?.ResRestyp.BO_RESURSTYP_NAMN === resType.namn &&
          item2.Resursnamn &&
          resursBokningsbar(item2, userSign)
        ) {
          acc.push({
            name: item2.Resursnamn,
            value: item2.BO_RESURS_ID,
            id: item2.BO_RESURS_ID,
            checked: checkedResources.includes(item2.id),
            data: bokningsrubrikFromDbResurs(item2),
          });
        }
        return acc;
      }, []) ?? [];

    subList.push({
      name: resType.namn,
      value: resType.id,
      subList: [...resSubTypes, ...resources],
      id: resType.id,
    });
  });

  return subList.filter(item => item.subList?.length !== 0);
};

const updateBokningsrubriker = (
  resurser: IBokningsrubrik[],
  resursTypContext: IResursTypContext,
  formContext: IBusinessDataFormContext,
  bastyp: string,
  currentBokningsRubrikPosnr?: number[],
  allAktTypResursRubriker?: IAktTypResursRubrik[],
  fullResourceData?: ResursQuery_db_BOResurs_All[],
  userSign?: string
) => {
  const usedPosnr = [...(currentBokningsRubrikPosnr ?? [])];

  if (
    !formContext?.formState.loading &&
    formContext &&
    resurser.forEach !== undefined
  ) {
    const currentValues = formContext.formState.currentValues;

    resurser.forEach(resurs => {
      const possibleAktTypResursRubriker = allAktTypResursRubriker
        ? mergeSort(
            allAktTypResursRubriker
              .map(rub => ({
                rubrik: rub,
                depth: resursTypContext.isOfType(
                  resurs.resursTyp,
                  rub.foreslagenResTyp
                ),
              }))
              .filter(rub => rub.depth > 0),
            (a, b) => a.depth - b.depth
          )
        : [];

      if (
        !currentValues?._Associations?.AktBokRubrik?.find(
          (value: AktivitetType__Associations_AktBokRubrik) =>
            value._Associations?.Resurs?.id === resurs.resursId
        )
      ) {
        const aktTypResursRubrik = possibleAktTypResursRubriker?.find(
          rub => !usedPosnr?.find(posNr => posNr === rub.rubrik.posNr)
        );
        const posNr = aktTypResursRubrik?.rubrik.posNr;
        if (posNr) usedPosnr.push(posNr);

        formContext.update(
          getAddResursUpdateArray(
            resurs.resursNamn,
            resurs.BO_RESURS_ID,
            resurs.resursId,
            resurs.resursTyp,
            bastyp,
            currentValues?.FRANDATUM,
            currentValues?.TILLDATUM,
            currentValues?.FRANTID,
            currentValues?.TILLTID,
            posNr
          )
        );

        if (posNr === 1) {
          const forsamling = !formContext?.formState?.currentValues
            ?._Associations?.Forsamling?.id
            ? { id: resurs.platsId, namn: resurs.resursNamn }
            : undefined;
          if (forsamling?.id) {
            formContext.update(
              getUpdateForsamlingArray(forsamling.id, forsamling.namn)
            );
          }
        }
      }
    });
  }

  if (formContext && resurser?.find !== undefined) {
    const currentValues = formContext.formState.currentValues;
    const resourceLookup = new Map(
      fullResourceData?.map(resource => [resource.id, resource]) ?? []
    );

    currentValues?._Associations?.AktBokRubrik?.filter(
      (aktBokRubrik: AktivitetType__Associations_AktBokRubrik) =>
        aktBokRubrik._Associations?.Resurs?._Associations?.ResRestyp.Bastyp ===
        bastyp
    ).forEach((value: AktivitetType__Associations_AktBokRubrik) => {
      if (
        value._Associations?.Resurs?.id &&
        !resurser.find(
          (option: IBokningsrubrik) =>
            value._Associations?.Resurs?.id === option.resursId
        )
      ) {
        const fullResource = resourceLookup.get(
          value._Associations?.Resurs?.id
        );
        if (fullResource && resursBokningsbar(fullResource, userSign)) {
          formContext.update(getDeleteResursUpdateArray(value.id));
        }
      }
    });
  }
};

// ---------------------------------------------------------------------------
// TypedResursValjare — extracted as a proper React component
// ---------------------------------------------------------------------------

interface ITypedResursValjareProps {
  bastyp: string;
  iconType: string | undefined;
  data: ResursQuery | undefined;
  userSign: string | undefined;
  resursTypContext: IResursTypContext;
  formContext: IBusinessDataFormContext<any>;
  allSections: ITreeSection<any>[];
  rightMargin: boolean | undefined;
  checkDoubleBookedResources: IFieldValidation | undefined;
  disableMode: boolean;
  currentBokningsRubrikPosnr?: number[];
  allAktTypResursRubriker?: IAktTypResursRubrik[];
  userSettings?: MeQuery_me | undefined;
  nodeData?: AktivitetQuery_node_TEDBOAktivitet | null | undefined;
  me_data?: MeQuery | undefined;
  availableRoutines?: IRoutineAvailability | undefined;
}

const TypedResursValjare = ({
  bastyp,
  iconType,
  data,
  userSign,
  resursTypContext,
  formContext,
  allSections,
  rightMargin,
  checkDoubleBookedResources,
  disableMode,
  currentBokningsRubrikPosnr,
  allAktTypResursRubriker,
  userSettings,
  nodeData,
  me_data,
  availableRoutines,
}: ITypedResursValjareProps) => {
  const { classes } = useStyles();
  const isSmallScreen = useIsSmallScreen();
  const dataFormContext = useContext(BusinessDataFormContext);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [deviatingTimeOpen, setDeviatingTimeOpen] = useState(false);
  const [sections, setSections] = useState<ITreeSection<IBokningsrubrik>[]>(
    clone(allSections.filter(section => section.name === bastyp))
  );

  const currentDataValues = formContext?.formState.currentValues;
  const bastypLabel = bastyp === 'Övrigt' ? 'Övriga' : bastyp + 'er';
  const isPersonInputField = bastypLabel === 'Personer';

  // Build the flat resources list for the autocomplete
  const resources: IBokningsrubrik[] = useMemo(() => {
    if (data === undefined) return [];

    const baseResTypes = resursTypContext
      .resTypes()
      .filter(resType => resType.bastyp === bastyp);

    return mergeSort(
      data?.db?.BOResurs_All ?? [],
      (a, b) => (a.PRIO_ORDNING ?? 1000) - (b.PRIO_ORDNING ?? 1000)
    )
      .filter(resource => {
        const resRestypNamn =
          resource?._Associations?.ResRestyp?.BO_RESURSTYP_NAMN || '';
        const resourceBastyp = resource?._Associations?.ResRestyp?.Bastyp ?? '';
        return (
          ((bastyp === 'Övrig' &&
            !['Plats', 'Person'].includes(resourceBastyp)) ||
            resourceBastyp === bastyp ||
            baseResTypes.some(resType => resType.namn === resRestypNamn)) &&
          resursBokningsbar(resource, userSign)
        );
      })
      .sort((a, b) => {
        const nameA = a?._Associations?.ResRestyp?.BO_RESURSTYP_NAMN || '';
        const nameB = b?._Associations?.ResRestyp?.BO_RESURSTYP_NAMN || '';
        return (
          baseResTypes.findIndex(rt => rt.namn === nameA) -
          baseResTypes.findIndex(rt => rt.namn === nameB)
        );
      })
      .map(resource => {
        const resRestypNamn =
          resource?._Associations?.ResRestyp?.BO_RESURSTYP_NAMN || '';
        return {
          resursText: `${resource.BO_RESURS_NAMN} (${resRestypNamn})`,
          resursNamn: resource.BO_RESURS_NAMN,
          BO_RESURS_ID: resource.BO_RESURS_ID,
          resursId: resource.id,
          resursTyp: resRestypNamn,
          platsId: resource._Associations?.Forsamling?.id || '',
        };
      });
  }, [data, bastyp, resursTypContext, userSign]);

  // Current bookings for this bastyp
  const bokningsrubriker: IBokningsrubrik[] = useMemo(() => {
    return (
      currentDataValues?._Associations?.AktBokRubrik?.reduce(
        (
          res: IBokningsrubrik[],
          item: AktivitetType__Associations_AktBokRubrik
        ) => {
          const itemBastyp =
            item._Associations?.Resurs?._Associations?.ResRestyp.Bastyp;
          if (
            (bastyp === 'Övrig' &&
              itemBastyp !== 'Plats' &&
              itemBastyp !== 'Person') ||
            itemBastyp === bastyp
          ) {
            res.push(bokningsrubrikFromDbBokningsrubrik(item, resources));
          }
          return res;
        },
        []
      ) ?? []
    );
  }, [bastyp, currentDataValues?._Associations?.AktBokRubrik, resources]);

  const boknGruppIsEmpty =
    !formContext?.formState?.loading &&
    !!currentDataValues &&
    !currentDataValues?._Associations?.Bokningsgrupp?.id;

  const boknGruppWasCleared =
    !!formContext?.formState?.editMode &&
    boknGruppIsEmpty &&
    formContext?.formState?.values?._Associations?.Bokningsgrupp_Ref === null;

  // Delete all bookings when booking group becomes empty — use effect to avoid side effect in render
  useEffect(() => {
    if (boknGruppWasCleared) {
      bokningsrubriker.forEach((value: IBokningsrubrik) => {
        if (value.rubrikId && formContext) {
          formContext.update(getDeleteResursUpdateArray(value.rubrikId));
        }
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bokningsrubriker, boknGruppWasCleared]);

  const editResursChange = useCallback(
    (resurser: IBokningsrubrik[]) => {
      updateBokningsrubriker(
        resurser,
        resursTypContext,
        formContext,
        bastyp,
        currentBokningsRubrikPosnr,
        allAktTypResursRubriker,
        data?.db?.BOResurs_All ?? undefined,
        userSign
      );
    },
    [
      bastyp,
      allAktTypResursRubriker,
      currentBokningsRubrikPosnr,
      data?.db?.BOResurs_All,
      formContext,
      resursTypContext,
      userSign,
    ]
  );

  const editResursDelete = (tagId: string) => () => {
    if (formContext) {
      const resurser = bokningsrubriker.filter(v => v.rubrikId !== tagId);
      updateBokningsrubriker(
        resurser,
        resursTypContext,
        formContext,
        bastyp,
        currentBokningsRubrikPosnr,
        allAktTypResursRubriker,
        data?.db?.BOResurs_All ?? undefined,
        userSign
      );
    }
  };

  const getAktBokRubrikString = (value: IBokningsrubrik): string => {
    const found = currentDataValues?._Associations?.AktBokRubrik?.find(
      (x: AktivitetType__Associations_AktBokRubrik) =>
        x._Associations?.Resurs?.id === value.resursId
    );
    if (
      !found ||
      (found.FRANTID === currentDataValues.FRANTID &&
        found.TILLTID === currentDataValues.TILLTID)
    ) {
      return '';
    }
    return `${timeToStr2(found?.FRANTID)}-${timeToStr2(found?.TILLTID)}`;
  };

  const getUncheckCurrentUrval = (
    items: ITreeSection<any>[]
  ): ITreeSection<any>[] => {
    for (const item of items) {
      if ('checked' in item) {
        if (item.checked && !item.hidden) item.checked = false;
      } else {
        getUncheckCurrentUrval(item.subList!);
      }
    }
    return items;
  };

  const getHiddenCheckedResources = (
    items: ITreeSection<any>[]
  ): ITreeSection<any>[] => {
    for (const item of items) {
      if ('checked' in item) {
        if (item.checked) item.hidden = item.checked;
      } else {
        getHiddenCheckedResources(item.subList!);
      }
    }
    return items;
  };

  const openDialog = () => setDialogOpen(true);
  const closeResursDialog = () => setDialogOpen(false);

  const selectResursDialog = () => {
    updateBokningsrubriker(
      getBokningsrubrikerFromSections(sections),
      resursTypContext,
      dataFormContext,
      bastyp,
      currentBokningsRubrikPosnr,
      allAktTypResursRubriker,
      data?.db?.BOResurs_All ?? undefined,
      userSign
    );
    closeResursDialog();
  };

  const Avmarkera = () => setSections(getUncheckCurrentUrval(clone(sections)));

  // Person-specific logic
  const personResource = useMemo(
    () => resources.find(r => r.BO_RESURS_ID === userSettings?.userBookingID),
    [resources, userSettings?.userBookingID]
  );

  const personResourcePermissions = useMemo(
    () =>
      data?.db?.BOResurs_All?.find(
        r => r.BO_RESURS_ID === personResource?.BO_RESURS_ID
      ),
    [data?.db?.BOResurs_All, personResource?.BO_RESURS_ID]
  );

  const isPersonAlreadyAdded = bokningsrubriker.some(
    item => item.BO_RESURS_ID === personResource?.BO_RESURS_ID
  );

  const canShowAddSelf =
    isPersonInputField &&
    personResourcePermissions?._Associations?.Anvandare?.SIGNATURE &&
    personResourcePermissions?.BOKBAR_AKTIVITET &&
    isPersonResourceBookable(personResourcePermissions, userSign) &&
    canUserInteractWithBooking(me_data, nodeData, availableRoutines) &&
    !isPersonAlreadyAdded;

  const handleAddSelf = () => {
    if (!personResource) return;
    const updated = [...bokningsrubriker, personResource];
    if (
      updated.length > 0 &&
      personResourcePermissions &&
      personResourcePermissions._Associations?.Anvandare?.SIGNATURE &&
      personResourcePermissions.BOKBAR_AKTIVITET &&
      isPersonResourceBookable(personResourcePermissions, userSign) &&
      canUserInteractWithBooking(me_data, nodeData, availableRoutines)
    ) {
      updateBokningsrubriker(
        updated,
        resursTypContext,
        formContext,
        bastyp,
        currentBokningsRubrikPosnr,
        allAktTypResursRubriker,
        data?.db?.BOResurs_All ?? undefined,
        userSign
      );
    }
  };

  const isDisabled =
    boknGruppIsEmpty || !formContext?.formState?.editMode || disableMode;

  return (
    <>
      {canShowAddSelf ? (
        <EDIconButton
          icon={'add_profile'}
          tooltip="Boka in mig själv"
          iconSize="medium"
          noLeftMargin
          marginRight={1.5}
          marginTop={2}
          padding={0}
          color="primary"
          disabled={
            isDisabled ||
            !canUserInteractWithBooking(me_data, nodeData, availableRoutines)
          }
          onClick={handleAddSelf}
        />
      ) : (
        <EDIcon
          marginRight={rightMargin ? 1 : undefined}
          marginTop={1}
          iconType={iconType as IconType}
        />
      )}

      <EDAutoCompleteEdit<IBokningsrubrik, true>
        label={bastypLabel}
        value={bokningsrubriker ?? []}
        options={resources}
        getOptionLabel={option => option.resursText}
        getOptionSelected={(option, value) =>
          option.resursId === value.resursId
        }
        className={classes.ResourceAddBtnWidth}
        onChange={editResursChange}
        fullWidth={true}
        multiple
        disabled={isDisabled}
        renderTags={values =>
          sortBokningsrubriker(values).map(value => {
            const isCurrentUserResource =
              value.BO_RESURS_ID === userSettings?.userBookingID;
            return (
              <EDChip
                margin={0.5}
                onDelete={
                  formContext?.formState.editMode
                    ? editResursDelete(value.rubrikId || '')
                    : undefined
                }
                key={value.rubrikId || ''}
                chipRadioButtonSelectedChip={
                  isPersonInputField && isCurrentUserResource
                }
                label={
                  <div
                    style={{
                      display: 'flex',
                      flexDirection: 'row',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <EDBox
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        lineHeight: '1em',
                        margin: '0px',
                      }}
                    >
                      <div>{value.resursNamn}</div>
                      <EDBox sx={{ fontSize: 10 }}>
                        {value.resursTyp} {getAktBokRubrikString(value)}
                      </EDBox>
                    </EDBox>
                    {getAktBokRubrikString(value) !== '' && (
                      <EDIconButton
                        icon={'deviating_time'}
                        tooltip="Avvikande tid"
                        onClick={() => setDeviatingTimeOpen(true)}
                        noRightMargin
                        marginLeft={1}
                        padding={0.5}
                      />
                    )}
                  </div>
                }
                disabled={
                  isCurrentUserResource
                    ? false
                    : !value.resourceInList || disableMode
                }
                disabledReason={
                  isCurrentUserResource
                    ? undefined
                    : 'Du har inte behörighet att boka denna resurs'
                }
              />
            );
          })
        }
        classes={{ inputRoot: classes.chipMarginTop }}
        warningMessage={
          checkDoubleBookedResources &&
          `Dubbelbokning: ${checkDoubleBookedResources.message}`
        }
        testId={'arAutoComplete'}
      />

      <EDButton
        alignLeft
        padding={1}
        marginTop={1}
        onClick={openDialog}
        label={isSmallScreen ? '' : `Välj ${bastypLabel}`}
        resourceBtnStyling={true}
        icon={<EDIcon iconType="add" color="inherit" />}
        flat
        size="small"
        data-inline="true"
        disabled={isDisabled}
      >
        <EDDialog
          fullWidth={true}
          open={dialogOpen}
          onClose={closeResursDialog}
          footerActions={[
            {
              label: 'Avmarkera',
              onClick: Avmarkera,
              buttonProps: {
                disabled:
                  getBokningsrubrikerFromSections(sections).length === 0,
                disabledReason: 'Inga resurser valda',
              },
            },
            { label: 'Avbryt', onClick: closeResursDialog },
            { label: 'Använd', onClick: selectResursDialog },
          ]}
          onEnter={() => {
            const baseSection = clone(
              allSections.filter(s => s.name === bastyp)[0]
            );
            let resTypSections: ITreeSection<IBokningsrubrik>[] = [];
            if (baseSection?.subList) {
              resTypSections = getHiddenCheckedResources(baseSection.subList);
            }

            const baseResTypes = resursTypContext
              .resTypes()
              .filter(rt => rt.bastyp === bastyp);
            const orderedRes = mergeSort(
              data?.db?.BOResurs_All ?? [],
              (a, b) => (a.PRIO_ORDNING ?? 1000) - (b.PRIO_ORDNING ?? 1000)
            ).filter(
              section => section?._Associations?.ResRestyp?.Bastyp === bastyp
            );

            // eslint-disable-next-line aveny/array-sort-rule
            resTypSections.sort(
              (a, b) =>
                baseResTypes.findIndex(rt => rt.namn === a.name) -
                baseResTypes.findIndex(rt => rt.namn === b.name)
            );

            resTypSections.forEach(resurs =>
              resurs.subList?.sort((a: { id: string }, b: { id: string }) => {
                return (
                  orderedRes.findIndex(s => s.BO_RESURS_ID === a.id) -
                  orderedRes.findIndex(s => s.BO_RESURS_ID === b.id)
                );
              })
            );

            setSections(resTypSections);
          }}
          title={`Välj ${bastypLabel}`}
        >
          <EDTreeSelect
            sections={sections}
            searchValue={''}
            sectionsUpdate={setSections}
          />
        </EDDialog>
      </EDButton>

      <ResursAvvikandeTid
        formContext={formContext}
        deviatingTimeOpen={deviatingTimeOpen}
        setDeviatingTimeOpen={setDeviatingTimeOpen}
      />
    </>
  );
};

// ---------------------------------------------------------------------------
// Root component
// ---------------------------------------------------------------------------

const RESOURCE_TYPES: Array<{
  bastyp: string;
  iconType: string;
  validationField: string;
}> = [
  { bastyp: 'Plats', iconType: 'room', validationField: '_PLATSER' },
  { bastyp: 'Person', iconType: 'person', validationField: '_PERSONER' },
  { bastyp: 'Övrigt', iconType: 'post_add', validationField: '_OVRIGA' },
];

const ActivitetResursValjare = (props: IProps) => {
  const activeDatabaseContext = useContext(ActiveDatabaseContext);
  const dataFormContext = useContext(BusinessDataFormContext);
  const routineAvailabilityContext = useContext(RutinSettingsContext);
  const resursTypContext = useContext(ResursTypContext);

  const routineID = routineAvailabilityContext.activeRoutine?.routine.rutinID;

  const resursQueryOptions = useMemo(
    () => ({
      variables: {
        dbname: activeDatabaseContext.activeDatabase ?? '',
        routineID,
      },
      skip: !open || !activeDatabaseContext.activeDatabase,
    }),
    [activeDatabaseContext.activeDatabase, routineID]
  );

  const { data: resurs_data } = useQuery<ResursQuery, ResursQueryVariables>(
    resursQuery,
    resursQueryOptions
  );

  const allSections = useMemo(() => {
    if (!resurs_data) return [];

    const checkedResources = getCheckedResourcesFromBokningsrubriker(
      dataFormContext?.formState?.currentValues?._Associations?.AktBokRubrik ??
        []
    );

    if (!resurs_data?.db?.BOResurs_All) {
      return [{ name: 'Inga resurser hittades', value: '', id: '' }];
    }

    return getUniqResourceBaseTypes(resurs_data.db.BOResurs_All).map(
      (item: any) => ({
        name: item.name,
        value: item.value,
        id: item.value,
        subList: GetSubList(
          resurs_data,
          resursTypContext.resTypes(),
          checkedResources,
          item.name,
          props.userSign
        ),
        open: true,
      })
    );
  }, [
    resurs_data,
    dataFormContext?.formState?.currentValues,
    resursTypContext,
    props.userSign,
  ]);

  const allAktTypResursRubriker =
    props.allAktResursRubriker ??
    resursTypContext.aktTypRubriker(
      dataFormContext?.formState?.currentValues?._typ,
      dataFormContext?.formState?.currentValues?._Associations?.Bokningsgrupp
        ?.id
    );

  return (
    <>
      {RESOURCE_TYPES.map(({ bastyp, iconType, validationField }) => {
        const checkDoubleBookedResources =
          dataFormContext?.formState.validation.find(
            x => x.fieldName === validationField
          );
        return (
          <div key={bastyp} style={{ display: 'flex', alignItems: 'center' }}>
            <TypedResursValjare
              bastyp={bastyp}
              iconType={iconType}
              data={resurs_data}
              userSign={props.userSign}
              resursTypContext={resursTypContext}
              formContext={props.formContext}
              allSections={allSections}
              rightMargin={props.rightMargin}
              checkDoubleBookedResources={checkDoubleBookedResources}
              disableMode={props.disableMode}
              currentBokningsRubrikPosnr={props.currentBokningsRubrikPosnr}
              allAktTypResursRubriker={allAktTypResursRubriker}
              userSettings={props.userSettings}
              nodeData={props.nodeData}
              me_data={bastyp === 'Person' ? props.me_data : undefined}
              availableRoutines={
                bastyp === 'Person' ? props.availableRoutines : undefined
              }
            />
          </div>
        );
      })}
    </>
  );
};

export default ActivitetResursValjare;
